import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useHistory } from 'react-router';
import axios from 'axios';
import { Tabs, Card, message } from 'components';
import styled from 'styled-components';
import {
  getSupraLockboxActivities,
  getSupraLockboxById,
  ParsedSupraLockboxJsonType,
} from 'api/SupraLockboxController';
import { useLocation, useParams } from 'react-router-dom';
import { AuthContext } from 'contexts';
import { debounce, throttle } from 'lodash';
import { LockboxesActivityFilterOptions } from 'api/get/getListingAccessActivities';
import { featureEnabled, isAuthForLocation, isAuthForRole } from 'utils';
import {
  isBranchAdmin,
  isGlobalAdmin,
  isManager,
  isOwner,
  isTeamAdmin,
  UserRoles,
} from '@bb/users';
import { SupraSystemSubscriptionType } from '@bb/domain/dist/types/SupraSystemSubscription';
import DetailsPageHeader from './DetailsPageHeader';
import LockboxActivityTable from '../LockboxesActivityPage/LockboxActivityTable';
import ShacklePermissionTable from './ShacklePermissionTable';
import { useBreakpoint } from '../../../hooks';
import LockboxesActivityRightHeader from '../LockboxesActivityPage/LockboxesActivityRightHeader';

type PageTabs = 'activity' | 'shackle';

const SupraLockBoxDetailsPage: React.FC = () => {
  const history = useHistory();
  const { md } = useBreakpoint();

  const { auth: authUser } = useContext(AuthContext);
  const isMlsUser = authUser?.org.type === 'mlsStaff';

  const isMlsPath = useLocation().pathname.split('/')[1] === 'mls';

  const { lockboxId } = useParams<{ lockboxId?: string }>();
  const [loading, setLoading] = React.useState(false);
  const [lockbox, setLockbox] = useState<ParsedSupraLockboxJsonType>();
  const [lockboxActivities, setLockboxActivities] = useState<todo[]>([]);
  const [selectedTab, setSelectedTab] = useState<PageTabs>('activity');

  const [total, setTotal] = useState(0);
  const [initialTotal, setInitialTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<LockboxesActivityFilterOptions>({
    activities: [],
    lockboxSerialNumbers: [],
    startDate: null,
    endDate: null,
    userIds: [],
    listingIds: [],
    keyTypes: [],
    lockboxIds: [lockboxId || ''],
    search: '',
  });
  const [sortOptions, setSortOptions] = useState<{
    sort: string;
    order: 'ascend' | 'descend';
  }>({
    sort: '',
    order: 'descend',
  });
  const [page, setPage] = useState<number>(1);

  const fetchLockboxData = useCallback(async (lbId: string) => {
    setLoading(true);
    try {
      const data = await getSupraLockboxById(lbId);
      setLockbox(data);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLockboxActivityData = useCallback(
    async (
      activityFilters?: LockboxesActivityFilterOptions,
      sort?:
        | {
            sort: string;
            order: 'ascend' | 'descend';
          }
        | undefined,
      activityPage?: number,
    ) => {
      const cancelToken = axios.CancelToken.source();

      setLoading(true);
      try {
        const { activities, count } = await getSupraLockboxActivities({
          filters: activityFilters || filters,
          sort: sort || sortOptions,
          page: activityPage || page,
          cancelToken: cancelToken.token,
        });

        setInitialTotal(count);
        setLockboxActivities(activities);
        cancelToken.cancel();
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const lockboxSlowAssignmentEnhancementsEnabled = featureEnabled(
    'LockboxSlowAssignmentEnhancements',
  );
  const hasPermission = useMemo(() => {
    const authIsGlobalAdmin = isGlobalAdmin(authUser as any);
    const authIsOwner = isOwner(authUser as any);
    const authIsManager = isManager(authUser as any);
    const authIsBranchAdmin = isBranchAdmin(authUser as any);
    const authIsTeamAdmin = isTeamAdmin(authUser as any);

    if (lockbox?.owner === authUser.id) {
      return true;
    }

    /**
     * Updated requirements for lockbox assignments based on BET-1665
     * https://honeywell.atlassian.net/browse/BET-1665
     */
    if (lockboxSlowAssignmentEnhancementsEnabled) {
      if (
        // Global Admins can change lockbox assignments if owner is within their org. This doesn't apply if the assignee is the authUser themselves.
        ((authIsGlobalAdmin || authIsOwner || authIsManager) &&
          lockbox?.parsedOwner?.orgId === authUser.org.id &&
          !(
            lockbox.assigneeType === 'user' && lockbox.assignee === authUser.id
          )) ||
        /**
         * Branch Admins can change lockbox assignments if owner is within their org
         * AND owner location is within their authorized locations. This doesn't apply
         * if the assignee is the authUser themselves.
         */
        (authIsBranchAdmin &&
          lockbox?.parsedOwner?.orgId === authUser.org.id &&
          isAuthForLocation(authUser, {
            location:
              lockbox.ownerType === 'office'
                ? lockbox.ownerBranchIndex
                : lockbox.parsedOwner?.location,
            locations: [],
          }) &&
          !(
            lockbox.assigneeType === 'user' && lockbox.assignee === authUser.id
          )) ||
        (authIsTeamAdmin &&
          lockbox?.ownerType === 'user' &&
          isAuthForRole({
            auth: authUser,
            org: lockbox.parsedOwner?.orgId,
            teams: lockbox.parsedOwner?.teams || [],
          }) &&
          !(
            lockbox.assigneeType === 'user' && lockbox.assignee === authUser.id
          ))
      )
        return true;

      /**
       * If authUser is in the same brokerage as the assignee entity, they cannot
       * change the lockbox assignment regardless of their system role.
       */
      if (
        (lockbox?.assigneeType === 'office' ||
          lockbox?.assigneeType === 'user') &&
        lockbox?.parsedAssignee?.orgId === authUser.org.id
      )
        return false;
    }

    // If user is assignee they cannot change lockbox assignment regardless of their system role
    if (lockbox?.assigneeType === 'user' && lockbox?.assignee === authUser.id) {
      return false;
    }

    if (lockbox) {
      const locations = [
        (lockbox.ownerType === 'branch' || lockbox.ownerType === 'office') &&
          lockbox.ownerBranchIndex,
        (lockbox.assigneeType === 'branch' ||
          lockbox.assigneeType === 'office') &&
          lockbox.assigneeBranchIndex,
        lockbox.parsedOwner?.location,
        lockbox.parsedAssignee?.location,
      ].filter(Boolean) as string[];
      const teams = [
        ...(lockbox.parsedOwner?.teams || []),
        ...(lockbox.parsedAssignee?.teams || []),
      ];

      return isAuthForRole({ auth: authUser, locations, teams });
    }
    return false;
  }, [authUser, lockbox, lockboxSlowAssignmentEnhancementsEnabled]);

  const debouncedError = useCallback(
    throttle(msg => message.error(msg), 1500, { trailing: false }),
    [],
  );

  const handleError = async (err: any) => {
    if (axios.isCancel(err)) return;
    if (err?.response?.status === 403) {
      if (!lockboxSlowAssignmentEnhancementsEnabled) {
        debouncedError('You do not have access to this page');
      } else {
        debouncedError('You no longer have access to this lockbox');
        // Give ES some time to update
        await new Promise(resolve => setTimeout(resolve, 1000));
        history.goBack();
      }
    } else {
      debouncedError('Something went wrong');
    }
  };

  useEffect(() => {
    const getLockboxData = async () => {
      if (!lockboxId) return;
      await fetchLockboxData(lockboxId);
      await fetchLockboxActivityData();
    };
    getLockboxData();
  }, []);

  useEffect(() => {
    const getLockboxActivities = async () => {
      if (!filters.lockboxIds?.length) return;
      await fetchLockboxActivityData(filters, sortOptions, page);
    };
    getLockboxActivities();
  }, [filters, sortOptions, page]);

  const debouncedSearch = React.useMemo(
    () =>
      debounce(text => {
        setFilters({ ...filters, search: text });
      }, 300),
    [filters],
  );

  React.useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [],
  );

  useEffect(() => {
    debouncedSearch(searchText);
  }, [searchText]);

  if (!lockboxId || (isMlsPath && !isMlsUser)) return <></>;
  return (
    <>
      <DetailsPageHeader
        fetchLockboxData={fetchLockboxData}
        fetchLockboxActivityData={fetchLockboxActivityData}
        lockbox={lockbox}
        loading={loading}
        isMlsUser={isMlsUser}
        hasPermission={hasPermission}
        isLimitedUser={
          authUser?.supraSubscription?.type ===
          SupraSystemSubscriptionType.LIMITED
        }
      />
      <StyledCard>
        <StyledTabs
          activeKey={selectedTab}
          onTabClick={(key: PageTabs) => {
            history.replace({ search: `?tab=${key}` });
            setSelectedTab(key);
          }}
          tabBarStyle={{
            margin: 0,
            padding: `0 ${md ? 20 : 10}px`,
          }}
          items={[
            {
              key: 'activity',
              label: 'Activity',
              children: (
                <>
                  <LockboxesActivityRightHeader
                    filters={filters}
                    sort={sortOptions}
                    page={page}
                    setPage={setPage}
                    total={total}
                    initialTotal={initialTotal}
                    searchText={searchText}
                    setSearchText={setSearchText}
                    setFilters={setFilters}
                    isLoading={false}
                  />

                  <LockboxActivityTable
                    filters={filters}
                    sortOptions={sortOptions}
                    setFilters={setFilters}
                    setSortOptions={setSortOptions}
                    activities={lockboxActivities}
                    loading={loading}
                    totalCount={total}
                    page={page}
                    setPage={setPage}
                  />
                </>
              ),
            },
            ...(isMlsUser ||
            hasPermission ||
            (lockbox?.owner && lockbox.owner === authUser.id) ||
            (lockbox?.assignee && lockbox.assignee === authUser.id) ||
            /**
             * Admins can grant shackle permissions if assignee is within their brokerage.
             *
             * https://honeywell.atlassian.net/browse/BET-1665
             */
            (lockboxSlowAssignmentEnhancementsEnabled &&
              authUser.role >= UserRoles['Branch Admin'] &&
              lockbox?.parsedAssignee?.orgId === authUser.org.id)
              ? [
                  {
                    key: 'shackle',
                    label: 'Shackle Permission',
                    children: lockbox && (
                      <ShacklePermissionTable lockbox={lockbox} />
                    ),
                  },
                ]
              : []),
          ]}
          tabBarGutter={10}
        />
      </StyledCard>
    </>
  );
};

export default SupraLockBoxDetailsPage;

const StyledTabs = styled(Tabs)``;

const StyledCard = styled(Card)`
  margin: 20px 15px;
  .ant-card-body {
    padding: 0;
  }
`;
